// Numbers behind the Hosts dashboard: one row per host, built from the trip
// pipeline rows, the survey answers, the host profile and the payments.
// Pure functions so they are easy to test. Spec: docs/admin/trip-pipeline.md

import { STAGES, stageIndex, type Journey } from './tripPipeline';
import { average, npsScore, type AdminQuestion, type AdminSurvey } from './tripSurvey';

export interface HostProfileRow {
  profile_id: string;
  name: string | null;
  city: string | null;
  active: boolean;
  consecutive_missed_trips: number;
  suspended: boolean;
}

export interface PaymentRow {
  host_id: string | null;
  status: string;
  amount_cents: number | null;
  commission_cents: number | null;
  host_payout_cents: number | null;
  refunded_amount_cents: number | null;
}

export type HostHealth = 'ok' | 'watch' | 'risk';

export interface HostStat {
  host_id: string;
  name: string;
  city: string | null;
  active: boolean;
  trips: number;
  byStage: Record<string, number>;
  completed: number;
  flagged: number;
  /** Average days from "paid" to "host confirmed"; null with no data. */
  responseDays: number | null;
  rating: number | null;
  ratingCount: number;
  nps: number | null;
  reported: number;
  /** Travelers who booked this host more than once, out of travelers with a finished trip. */
  rebook: { repeat: number; of: number };
  health: HostHealth;
  gross: number;
  commission: number;
  payout: number;
}

const DAY = 86_400_000;
const live = (j: Journey) => j.kind === 'trip' && !!j.host_id && j.stage !== 'exited' && j.stage !== 'abandoned' && !j.archived;

function responseDays(rows: Journey[]): number | null {
  const gaps = rows
    .map((j) => (j.milestones.paid && j.milestones.host_confirmed
      ? (new Date(j.milestones.host_confirmed).getTime() - new Date(j.milestones.paid).getTime()) / DAY : null))
    .filter((v): v is number => v !== null && v >= 0);
  return gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : null;
}

export function healthOf(s: Pick<HostStat, 'rating' | 'ratingCount' | 'reported' | 'flagged'>, h: Pick<HostProfileRow, 'consecutive_missed_trips' | 'suspended'> | undefined): HostHealth {
  const lowRating = s.rating !== null && s.ratingCount >= 3 && s.rating < 3.5;
  if (h?.suspended || (h?.consecutive_missed_trips ?? 0) >= 2 || s.reported >= 2 || lowRating) return 'risk';
  const softRating = s.rating !== null && s.ratingCount >= 2 && s.rating < 4;
  if (s.flagged > 0 || s.reported === 1 || softRating || (h?.consecutive_missed_trips ?? 0) === 1) return 'watch';
  return 'ok';
}

export function hostStats(
  journeys: Journey[],
  profiles: HostProfileRow[],
  questions: AdminQuestion[],
  surveys: AdminSurvey[],
  payments: PaymentRow[],
): HostStat[] {
  const ratingQ = questions.find((q) => q.role === 'host_rating')?.id;
  const npsQ = questions.find((q) => q.role === 'nps')?.id;
  const troubleQ = questions.find((q) => q.role === 'trouble')?.id;

  const ids = new Set<string>(profiles.map((p) => p.profile_id));
  for (const j of journeys) if (live(j)) ids.add(j.host_id as string);

  return [...ids].map((id) => {
    const p = profiles.find((x) => x.profile_id === id);
    const mine = journeys.filter((j) => live(j) && j.host_id === id);
    const byStage: Record<string, number> = {};
    for (const j of mine) byStage[j.stage] = (byStage[j.stage] ?? 0) + 1;

    const answers = surveys.filter((s) => s.host_id === id).flatMap((s) => s.answers);
    const nums = (qid?: string) => answers.filter((a) => a.question_id === qid).map((a) => Number(a.value)).filter((n) => Number.isFinite(n));
    const ratings = nums(ratingQ);

    const finished = mine.filter((j) => stageIndex(j.stage) >= 4);
    const perTraveler = new Map<string, number>();
    for (const j of mine) if (stageIndex(j.stage) >= 2) perTraveler.set(j.traveler_id, (perTraveler.get(j.traveler_id) ?? 0) + 1);
    const finishedTravelers = new Set(finished.map((j) => j.traveler_id));

    const pay = payments.filter((x) => x.host_id === id && x.status === 'paid');
    const base = {
      rating: average(ratings), ratingCount: ratings.length,
      reported: answers.filter((a) => a.question_id === troubleQ && a.value === true).length,
      flagged: mine.filter((j) => j.flags.length > 0).length,
    };
    return {
      host_id: id,
      name: p?.name ?? mine.find((j) => j.host_name)?.host_name ?? id.slice(0, 8),
      city: p?.city ?? null,
      active: p?.active ?? true,
      trips: mine.length,
      byStage,
      completed: finished.length,
      responseDays: responseDays(mine),
      nps: npsScore(nums(npsQ)),
      rebook: { repeat: [...finishedTravelers].filter((t) => (perTraveler.get(t) ?? 0) >= 2).length, of: finishedTravelers.size },
      health: healthOf(base, p),
      gross: pay.reduce((s, x) => s + (x.amount_cents ?? 0) - (x.refunded_amount_cents ?? 0), 0),
      commission: pay.reduce((s, x) => s + (x.commission_cents ?? 0), 0),
      payout: pay.reduce((s, x) => s + (x.host_payout_cents ?? 0), 0),
      ...base,
    };
  });
}

export interface HostTotals {
  hosts: number;
  withTrips: number;
  trips: number;
  needsAttention: number;
  rating: number | null;
  atRisk: number;
}

export function totals(stats: HostStat[]): HostTotals {
  const rated = stats.filter((s) => s.rating !== null);
  const weighted = rated.reduce((n, s) => n + (s.rating as number) * s.ratingCount, 0);
  const count = rated.reduce((n, s) => n + s.ratingCount, 0);
  return {
    hosts: stats.length,
    withTrips: stats.filter((s) => s.trips > 0).length,
    trips: stats.reduce((n, s) => n + s.trips, 0),
    needsAttention: stats.filter((s) => s.flagged > 0).length,
    rating: count ? Math.round((weighted / count) * 10) / 10 : null,
    atRisk: stats.filter((s) => s.health === 'risk').length,
  };
}

export type HostSort = 'trips' | 'name' | 'rating' | 'response' | 'health';
const HEALTH_RANK: Record<HostHealth, number> = { risk: 0, watch: 1, ok: 2 };

export function sortHosts(stats: HostStat[], by: HostSort): HostStat[] {
  const out = [...stats];
  const nullLast = (a: number | null, b: number | null, dir: 1 | -1) =>
    a === null ? (b === null ? 0 : 1) : b === null ? -1 : dir * (a - b);
  out.sort((a, b) => {
    switch (by) {
      case 'name': return a.name.localeCompare(b.name);
      case 'rating': return nullLast(a.rating, b.rating, -1);
      case 'response': return nullLast(a.responseDays, b.responseDays, 1);
      case 'health': return HEALTH_RANK[a.health] - HEALTH_RANK[b.health] || b.trips - a.trips;
      default: return b.trips - a.trips || a.name.localeCompare(b.name);
    }
  });
  return out;
}

/** Trips per host per calendar month of the trip start (or creation when there are no dates): rows are hosts, columns months. */
export function hostMonthGrid(journeys: Journey[], hostIds: string[], months: string[]): number[][] {
  return hostIds.map((id) => months.map((m) => journeys.filter((j) => {
    if (!live(j) || j.host_id !== id) return false;
    return (j.start_date ?? j.created_at).slice(0, 7) === m;
  }).length));
}

/** The last `n` YYYY-MM keys ending with the month of `now`, oldest first. */
export function lastMonths(n: number, now = new Date()): string[] {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (n - 1 - i), 1));
    return d.toISOString().slice(0, 7);
  });
}

export const money = (cents: number) => `€${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export { STAGES };
